import { Suspense, useState } from 'react';
import { useCollection } from '@/features/events/contexts/CollectionContext/useCollection';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext/useEffectiveExperience';
import { getIntegrationDestination } from '@/features/interactions/addToCalendar/destinations/registry';
import { usePickerIntegrations } from '@/features/interactions/addToCalendar/hooks/usePickerIntegrations';
import { useSubscribeExperienceToCalendar } from '@/features/interactions/addToCalendar/hooks/useSubscribeExperienceToCalendar';
import { useUnsubscribeExperienceFromCalendar } from '@/features/interactions/addToCalendar/hooks/useUnsubscribeExperienceFromCalendar';
import type {
  Destination,
  IntegrationPlatform,
} from '@/features/interactions/addToCalendar/types';
import { useCalendarAuth } from '@/features/interactions/addToGoogle/hooks/useCalendarAuth';
import { useAuthModal } from '@/features/magic/components/AuthModal';
import { PlatformTypes } from '@/types/gql.generated';
import { Button, Flex, Text, useToast } from '@/ui';
import { getName } from '@/utils/platformLib';
import { CalendarSubscriptionRow } from './CalendarSubscriptionRow';
import { UnsubscribeDialog } from './UnsubscribeDialog';

type Props = {
  platform: IntegrationPlatform;
};

/** Account-level deep link for a subscribed row's "Go to calendar". */
const CALENDAR_HREF: Partial<Record<IntegrationPlatform, string>> = {
  [PlatformTypes.GoogleIntegration]: 'https://calendar.google.com',
  [PlatformTypes.OutlookIntegration]: 'https://outlook.live.com/calendar',
};

export const CalendarSubscribeControl = ({ platform }: Props) => {
  const experience = useEffectiveExperience();
  const toast = useToast();
  const [destination, setDestination] = useState<Destination | null>(null);
  // The open dialog and the in-flight answer are one piece of state: the
  // dialog has to close on success itself, rather than waiting for the
  // experience refetch to unmount the row underneath it.
  const [stopping, setStopping] = useState<{
    id: string;
    choice: 'keep' | 'remove' | null;
  } | null>(null);

  const { integrations, isLoading, totalIntegrationCount } =
    usePickerIntegrations({ platform });
  const integration = getIntegrationDestination(platform)?.integration;
  const CalendarPicker = integration?.CalendarPicker;

  const { base } = useCollection();
  const { handleCalendarAuth } = useCalendarAuth({ base: base ?? undefined });
  const { open: openAuthModal } = useAuthModal();

  // usePickerIntegrations only drops accounts with no writable calendar, but
  // the picker also requires a valid status and scopes, so it can render
  // nothing while `integrations` is non-empty. Same predicate, off the
  // destination's own adapter.
  const usableIntegrations = integrations.filter(({ integration: account }) =>
    integration?.adapter.isSuitableIntegration(account)
  );

  // The server keys subscriptions on (experience, integration, calendar), so a
  // viewer can hold several per platform. Listing them all is what makes the
  // extra ones visible and stoppable.
  const subscriptions = experience.viewerCalendarSubscriptions.filter(
    (item) => item.platform === integration?.exportPlatform
  );

  const calendarNames = new Map(
    integrations
      .flatMap(({ suitableCalendars }) => suitableCalendars)
      .map((calendar) => [calendar.id, calendar.name])
  );

  const { subscribeToCalendar, isPending: isSubscribing } =
    useSubscribeExperienceToCalendar();
  const { unsubscribeFromCalendar } = useUnsubscribeExperienceFromCalendar();

  const stop = (subscriptionId: string, removeEvents: boolean) => {
    setStopping({
      id: subscriptionId,
      choice: removeEvents ? 'remove' : 'keep',
    });
    unsubscribeFromCalendar(subscriptionId, removeEvents, {
      onSuccess: () => {
        setStopping(null);
        toast.notify(
          removeEvents
            ? 'Stopped syncing and removed the events.'
            : 'Stopped syncing. The events already added stay put.'
        );
      },
      // Back to the question rather than closing: the subscription is still
      // live, so the user should be able to answer again.
      onError: () => {
        setStopping((current) =>
          current ? { ...current, choice: null } : null
        );
        toast.error('Couldn’t stop syncing. Try again.');
      },
    });
  };

  const handleSubscribe = () => {
    if (!destination) return;
    subscribeToCalendar(destination, {
      onSuccess: () => {
        setDestination(null);
        toast.notify('🎉 Syncing to your calendar.');
      },
      onError: () => toast.error('Couldn’t start syncing. Try again.'),
    });
  };

  const isPickedSubscribed = subscriptions.some(
    (item) => item.calendarId === destination?.calendar.id
  );

  const nameFor = (calendarId: string) =>
    calendarNames.get(calendarId) ?? getName(platform);

  const stoppingSubscription = subscriptions.find(
    (item) => item.id === stopping?.id
  );

  const list = subscriptions.length > 0 && (
    <Flex direction="column" gap={2}>
      {subscriptions.map((subscription) => (
        <CalendarSubscriptionRow
          calendarHref={CALENDAR_HREF[platform]}
          calendarName={nameFor(subscription.calendarId)}
          isDisabled={!!stopping && stopping.id !== subscription.id}
          key={subscription.id}
          onUnsubscribe={() =>
            setStopping({ id: subscription.id, choice: null })
          }
        />
      ))}
      {stoppingSubscription && (
        <UnsubscribeDialog
          calendarName={nameFor(stoppingSubscription.calendarId)}
          isOpen
          pendingChoice={stopping?.choice ?? null}
          onClose={() => setStopping(null)}
          onUnsubscribe={(removeEvents) =>
            stop(stoppingSubscription.id, removeEvents)
          }
        />
      )}
    </Flex>
  );

  // The picker renders nothing without a writable calendar, which would leave
  // a permanently disabled Subscribe and no way forward. Same split
  // BulkExportControl makes: nothing connected at all goes through auth, an
  // unusable connection needs its scopes granted again.
  if (!isLoading && usableIntegrations.length === 0) {
    const connect = () =>
      totalIntegrationCount === 0
        ? openAuthModal({
            reason: 'google-calendar-integration-multi',
            platform,
          })
        : handleCalendarAuth();

    return (
      <Flex direction="column" gap={2.5}>
        {list}
        <Button size="ah" variant="ahDark" w="100%" onClick={connect}>
          Connect {getName(platform)}
        </Button>
        <Text color="ink.muted" fontSize="ah-status" lineHeight={1.4}>
          {totalIntegrationCount === 0
            ? `Subscribing needs a ${getName(platform)} calendar you can add events to.`
            : `Reconnect to give Agenda Hero access to a ${getName(platform)} calendar it can add events to.`}
        </Text>
      </Flex>
    );
  }

  if (!CalendarPicker) return list || null;

  return (
    <Flex direction="column" gap={2.5}>
      {list}
      <Suspense fallback={null}>
        <CalendarPicker value={destination} onChange={setDestination} />
      </Suspense>
      <Button
        isDisabled={!destination || isPickedSubscribed}
        isLoading={isSubscribing}
        size="ah"
        variant="ahDark"
        w="100%"
        onClick={handleSubscribe}
      >
        Subscribe to All
      </Button>
      {/* Only the disabled-button explanation: the destination's description
          above the picker already says what subscribing does. */}
      {isPickedSubscribed && (
        <Text color="ink.muted" fontSize="ah-status" lineHeight={1.4}>
          That calendar is already syncing. Pick another to add it too.
        </Text>
      )}
    </Flex>
  );
};
